import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";

const CustomDropdown = ({
  value,
  onChange,
  options = [],
  placeholder = "Seçiniz...",
  searchPlaceholder = "Ara...",
  loading = false,
  error = false,
  errorMessage = "",
  disabled = false,
  className = "",
  showSearch = true,
  renderOption = null,
  renderValue = null,
  emptyMessage = "Veri bulunamadı",
  allOption = null, // { id: "all", label: "Tüm Seçenekler", description: "Tüm seçenekleri dahil et" }
  label,
  required = false,
  labelClassName,
  compact = false,
  /** Menüyü kutu dışında göstermek için portal kullanır; "top" = üstte (ayar kartları), "bottom" = altta (varsayılan) */
  menuPlacement = "bottom",
  /** Açılınca ve arama yapılınca çağrılır; verilirse client-side filtreleme devre dışı kalır */
  onSearch = null,
  /** onSearch debounce süresi (ms) */
  searchDebounce = 300,
  /** Çoklu seçim modu — true ise value array, onChange array döndürür */
  multi = false,
  /** multi=true iken trigger üzerinde gösterim için label çözücü; default: ilk N seçenek + "+K" */
  multiSummary = null,
  /** multi=true iken en üstteki hepsini seç / bırak satırının etiketi */
  selectAllLabel = "Tümünü Seç",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [menuPosition, setMenuPosition] = useState(null);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const debounceRef = useRef(null);

  // onSearch varsa açılınca çağır
  useEffect(() => {
    if (isOpen && onSearch) {
      onSearch("");
    }
  }, [isOpen]);

  // onSearch varsa debounce ile çağır
  const handleSearchChange = (val) => {
    setSearchTerm(val);
    if (onSearch) {
      setIsDebouncing(true);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setIsDebouncing(false);
        onSearch(val);
      }, searchDebounce);
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const isLoading = loading || isDebouncing;

  // onSearch varsa filtreleme dışarıdan yapılır, yoksa client-side
  const filteredOptions = onSearch
    ? options
    : options.filter(
        (option) =>
          option.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          option.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );

  const updateMenuPosition = () => {
    if (!dropdownRef.current) return null;
    const rect = dropdownRef.current.getBoundingClientRect();
    const gap = 4;
    const viewportH =
      window.innerHeight || document.documentElement.clientHeight || 0;
    // Menü yüksekliğini tahmin et: açıksa gerçek ölçüm, değilse seçenek sayısı.
    const measured = menuRef.current?.offsetHeight;
    const estimated =
      (showSearch ? 56 : 0) + Math.min(options.length || 1, 6) * 44 + 16;
    const menuH = measured || estimated;
    const spaceBelow = viewportH - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    // İstenen yerleşimi tercih et; ama o tarafta yer yoksa ve diğer taraf daha
    // genişse yönü çevir — menü viewport dışına taşıp kırpılmasın (ör. ayar
    // kartları grid'inin alt sırasındaki "Ekran plan rengi" dropdown'ı).
    let placement = menuPlacement;
    if (placement === "bottom" && spaceBelow < menuH && spaceAbove > spaceBelow) {
      placement = "top";
    } else if (placement === "top" && spaceAbove < menuH && spaceBelow > spaceAbove) {
      placement = "bottom";
    }
    // VIEWPORT'A KISITLA (2026-09-14): yön çevirme tek başına yetmiyordu.
    // Menü tetikleyicinin sol kenarından başlayıp onun genişliğinde açıldığı
    // için sayfa kenarındaki bir dropdown yatayda taşıp KIRPILIYOR, iki tarafta
    // da yer yoksa dikeyde ekran dışına çıkıyordu. Artık her iki eksende de
    // ekranın içine çekilir ve sığmıyorsa yüksekliği kısılıp kendi içinde
    // kaydırılır — menü asla ekran dışında kalmaz.
    const viewportW =
      window.innerWidth || document.documentElement.clientWidth || 0;
    const margin = 8;

    const width = Math.min(rect.width, Math.max(0, viewportW - margin * 2));
    const left = Math.min(
      Math.max(margin, rect.left),
      Math.max(margin, viewportW - width - margin),
    );

    if (placement === "top") {
      const available = Math.max(0, spaceAbove - margin);
      setMenuPosition({
        left,
        top: rect.top,
        width,
        maxHeight: available,
        bottom: undefined,
        transform: `translateY(calc(-100% - ${gap}px))`,
      });
    } else {
      const available = Math.max(0, spaceBelow - margin);
      setMenuPosition({
        left,
        top: rect.bottom + gap,
        width,
        maxHeight: available,
        bottom: undefined,
        transform: undefined,
      });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return;
    }
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen, menuPlacement]);

  /**
   * AÇILIR MENÜ, SOLDAKİ GEZİNME RAYININ ÜSTÜNE BİNMESİN.
   *
   * Masaüstü sidebar `fixed left-0` ve üzerine gelince 76px'ten 270px'e
   * genişleyip içeriğin ÜSTÜNE biniyor. Açık bir dropdown portal'ı z-[10000]
   * taşıdığı için rayın da üstünde kalıyordu.
   *
   * Çözüm z-index yarışı DEĞİL: dropdown modal içinde de kullanılıyor ve
   * modal'lar z-50'de; rayı dropdown'ın üstüne çıkarsak bu kez ray modal'ın
   * üstünde kalırdı. Bunun yerine ray açılırken menü kapanır — örtüşecek bir
   * şey kalmaz ve katman sırası bozulmaz.
   */
  useEffect(() => {
    if (!isOpen) return undefined;
    const close = () => {
      setIsOpen(false);
      setSearchTerm("");
    };
    window.addEventListener("app-menu-expanded", close);
    return () => window.removeEventListener("app-menu-expanded", close);
  }, [isOpen]);

  // Close dropdown when clicking outside (trigger veya portal menü dışına)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm("");
    }
  };

  const selectedValuesArray = useMemo(
    () => (multi ? (Array.isArray(value) ? value : []) : null),
    [multi, value]
  );
  const selectedIdsSet = useMemo(
    () => (multi ? new Set(selectedValuesArray.map((v) => v?.id ?? v)) : null),
    [multi, selectedValuesArray]
  );

  const handleOptionClick = (option) => {
    if (multi) {
      const exists = selectedIdsSet.has(option.id);
      const next = exists
        ? selectedValuesArray.filter((v) => (v?.id ?? v) !== option.id)
        : [...selectedValuesArray, option];
      onChange(next);
      // multi'de menüyü açık bırak
      return;
    }
    onChange(option);
    setIsOpen(false);
    setSearchTerm("");
  };

  // "Tümünü Seç" filtrelenmiş seçenekler üzerinde çalışır; "Tümü" etiketi (buildMultiTriggerLabel)
  // ise filtre yokken tüm options'a karşı kontrol eder — kullanıcı search ile daraltıp toggle ederse,
  // selection toplam sayıdan az kalır ve "Tümü" yerine özet etiket görünür (kasıtlı).
  const handleToggleAll = () => {
    if (!multi) return;
    const allSelected = filteredOptions.every((opt) => selectedIdsSet.has(opt.id));
    if (allSelected) {
      onChange([]);
    } else {
      onChange(filteredOptions);
    }
  };

  const allFilteredSelected = useMemo(
    () => multi && filteredOptions.length > 0 &&
      filteredOptions.every((opt) => selectedIdsSet.has(opt.id)),
    [multi, filteredOptions, selectedIdsSet]
  );
  const someFilteredSelected = useMemo(
    () => multi &&
      filteredOptions.some((opt) => selectedIdsSet.has(opt.id)) && !allFilteredSelected,
    [multi, filteredOptions, selectedIdsSet, allFilteredSelected]
  );

  const buildMultiTriggerLabel = () => {
    if (!selectedValuesArray || selectedValuesArray.length === 0) return null;
    const total = options.length;
    if (selectedValuesArray.length === total) return "Tümü";
    if (multiSummary) return multiSummary(selectedValuesArray);
    if (selectedValuesArray.length <= 2) {
      return selectedValuesArray.map((v) => v.label).join(", ");
    }
    const firstTwo = selectedValuesArray.slice(0, 2).map((v) => v.label).join(", ");
    const extra = selectedValuesArray.length - 2;
    return `${firstTwo} +${extra}`;
  };

  const selectedOption = multi
    ? null
    : (options.find((option) => option.id === value?.id) || value);

  return (
    <div className={`${compact ? "space-y-1.5" : "space-y-2"} ${className}`}>
      {label && (
        <label className={labelClassName || (compact ? "block text-sm font-medium text-gray-500" : "block text-sm font-semibold text-gray-700")}>
          {label}
          {required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      )}
      <div className="relative group" ref={dropdownRef}>
        <button
          type="button"
          onClick={toggleDropdown}
          disabled={disabled}
          className={`w-full bg-white border text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-left flex items-center justify-between ${compact
              ? "px-3 py-[9px] rounded-[10px] text-sm shadow-none hover:shadow-sm"
              : "px-4 py-3 rounded-2xl shadow-sm hover:shadow-md group-hover:shadow-lg"
            } ${error ? "border-red-300" : "border-gray-200"
            } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <span className={`${compact ? "text-sm" : ""} ${(multi ? selectedValuesArray.length > 0 : !!selectedOption) ? "text-gray-800" : "text-gray-400"}`}>
            {multi
              ? (buildMultiTriggerLabel() || placeholder)
              : (renderValue
                ? renderValue(selectedOption)
                : selectedOption?.label || placeholder)}
          </span>
          <div className="flex items-center gap-2">
            {loading && (
              <div className={`${compact ? "w-3 h-3" : "w-4 h-4"} border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin`}></div>
            )}
            <svg
              className={`${compact ? "w-4 h-4" : "w-5 h-5"} text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
                }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>

        {isOpen && menuPosition && typeof document !== "undefined" &&
          createPortal(
            <div
              ref={menuRef}
              className={`fixed z-[10000] flex flex-col bg-white border border-gray-200 ${compact ? "rounded-xl" : "rounded-2xl"} shadow-lg overflow-hidden`}
              style={{
                left: menuPosition.left,
                top: menuPosition.top,
                width: menuPosition.width,
                transform: menuPosition.transform,
                // Sığmıyorsa menü ekran dışına taşmak yerine kısalır; liste
                // zaten kendi içinde kayıyor (aşağıdaki overflow-y-auto).
                maxHeight: menuPosition.maxHeight,
                // En az 12rem okunur kalsın; ama kısıtlanmış genişliği
                // ASLA aşmasın, yoksa kenarda taşma geri gelir.
                minWidth: Math.min(192, menuPosition.width),
              }}
            >
              {/* Search Input */}
              {showSearch && (
                <div className="p-3 border-b border-gray-200">
                  <div className="relative">
                    <svg
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <input
                      type="text"
                      placeholder={searchPlaceholder}
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}

              {/* Dropdown List */}
              {/* Liste, kapsayıcıya kısıtlanmış yükseklik içinde kendi
                  kayar; `max-h-60` alt sınırı korunur ki kısıtlama yokken
                  menü sayfayı kaplamasın. */}
              <div className="max-h-60 overflow-y-auto flex-1 min-h-0">
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2 py-6">
                    <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                    <span className="text-sm text-gray-500">Yükleniyor...</span>
                  </div>
                ) : (
                  <>
                    {/* Multi: Tümünü Seç master checkbox */}
                    {multi && filteredOptions.length > 0 && (
                      <>
                        <button
                          type="button"
                          role="checkbox"
                          aria-checked={allFilteredSelected ? "true" : someFilteredSelected ? "mixed" : "false"}
                          onClick={handleToggleAll}
                          className="w-full px-4 py-2.5 text-left hover:bg-blue-50 transition-colors duration-200 flex items-center gap-3"
                        >
                          <span
                            className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                              allFilteredSelected
                                ? "bg-blue-600 border-blue-600"
                                : someFilteredSelected
                                  ? "bg-blue-200 border-blue-400"
                                  : "bg-white border-gray-300"
                            }`}
                          >
                            {allFilteredSelected && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                            {someFilteredSelected && (
                              <span className="block w-2 h-0.5 bg-blue-700 rounded" />
                            )}
                          </span>
                          <span className="text-sm font-semibold text-gray-700">{selectAllLabel}</span>
                        </button>
                        <div className="border-t border-gray-200" />
                      </>
                    )}

                    {/* All Option */}
                    {allOption && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleOptionClick(allOption)}
                          className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors duration-200 flex items-center justify-between ${value?.id === allOption.id
                              ? "bg-blue-50 text-blue-700"
                              : "text-gray-800"
                            }`}
                        >
                          <div className="flex-1">
                            <span className="font-medium block">
                              {allOption.icon && (
                                <span className="mr-2">{allOption.icon}</span>
                              )}
                              {allOption.label}
                            </span>
                            {allOption.description && (
                              <span className="text-xs text-gray-500 mt-0.5 block">
                                {allOption.description}
                              </span>
                            )}
                          </div>
                          {value?.id === allOption.id && (
                            <svg
                              className="w-4 h-4 text-blue-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </button>
                        <div className="border-t border-gray-200"></div>
                      </>
                    )}

                    {/* Options List */}
                    {filteredOptions.length > 0 ? (
                      filteredOptions.map((option) => {
                        const isSelected = multi
                          ? selectedIdsSet.has(option.id)
                          : value?.id === option.id;
                        return (
                          <button
                            key={option.id}
                            type="button"
                            {...(multi ? { "aria-pressed": isSelected } : {})}
                            onClick={() => handleOptionClick(option)}
                            className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors duration-200 flex items-center justify-between ${isSelected
                                ? "bg-blue-50 text-blue-700"
                                : "text-gray-800"
                              }`}
                          >
                            <div className="flex-1 flex items-center gap-3">
                              {multi && (
                                <span
                                  className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                                    isSelected ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"
                                  }`}
                                >
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </span>
                              )}
                              <div className="flex-1">
                                {renderOption ? (
                                  renderOption(option)
                                ) : (
                                  <>
                                    <span className="font-medium block">
                                      {option.icon && (
                                        <span className="mr-2">{option.icon}</span>
                                      )}
                                      {option.label}
                                    </span>
                                    {option.description && (
                                      <span className="text-xs text-gray-500 mt-0.5 block">
                                        {option.description}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            {!multi && isSelected && (
                              <svg
                                className="w-4 h-4 text-blue-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </button>
                        );
                      })
                    ) : (
                      <div className="px-4 py-6 text-center text-gray-500">
                        <svg
                          className="w-8 h-8 mx-auto mb-2 text-gray-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        <p className="text-sm">{emptyMessage}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>,
            document.body
          )
        }

        {/* Gradient Overlay */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/0 to-indigo-500/0 group-hover:from-blue-500/5 group-hover:to-indigo-500/5 transition-all duration-300 pointer-events-none"></div>
      </div>

      {/* Error Message */}
      {error && errorMessage && (
        <p className="text-red-500 text-xs mt-1">{errorMessage}</p>
      )}
    </div>
  );
};

export default CustomDropdown;
